import React, { useEffect, useState } from "react";
import { Button, Calendar, Flex, Typography, Select } from "antd";
import type { CalendarProps } from "antd";
import { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import "./index.scss";

dayjs.extend(localeData);

interface DateTimePickerProps {
  returnDateTime: (dateTime: string) => void;
  onClose?: any;
  previouslySelectedDate?: string | null;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  returnDateTime,
  onClose,
  previouslySelectedDate = null,
}) => {
  const [step, setStep] = useState<"date" | "time">("date");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [hour, setHour] = useState<number | null>(null);
  const [minute, setMinute] = useState<number | null>(null);
  const [ampm, setAmPm] = useState<"AM" | "PM">("AM");

  // Disable past dates
  const disabledDate = (current: Dayjs) => {
    return current < dayjs().startOf("day");
  };

  const getHourOptions = () => {
    return Array.from({ length: 12 }, (_, i) => {
      const hourVal = i + 1;

      if (selectedDate && selectedDate.isSame(dayjs(), "day")) {
        const current = dayjs();
        const selectedHour24 =
          ampm === "PM"
            ? hourVal === 12
              ? 12
              : hourVal + 12
            : hourVal === 12
            ? 0
            : hourVal;

        if (selectedHour24 < current.hour()) {
          return {
            label: hourVal.toString().padStart(2, "0"),
            value: hourVal,
            disabled: true,
          };
        }
      }

      return {
        label: hourVal.toString().padStart(2, "0"),
        value: hourVal,
      };
    });
  };

  const getMinuteOptions = () => {
    return Array.from({ length: 60 }, (_, i) => {
      if (
        selectedDate &&
        selectedDate.isSame(dayjs(), "day") &&
        hour !== null
      ) {
        const current = dayjs();
        const hour24 =
          ampm === "PM"
            ? hour === 12
              ? 12
              : hour + 12
            : hour === 12
            ? 0
            : hour;

        if (hour24 === current.hour() && i < current.minute()) {
          return {
            label: i.toString().padStart(2, "0"),
            value: i,
            disabled: true,
          };
        }
      }
      return { label: i.toString().padStart(2, "0"), value: i };
    });
  };

  const onSelectDate: CalendarProps<Dayjs>["onSelect"] = (date) => {
    setSelectedDate(date);
  };

  const handleNext = () => {
    if (step === "date" && selectedDate) {
      setStep("time");
    } else if (
      step === "time" &&
      selectedDate &&
      hour !== null &&
      minute !== null
    ) {
      // Convert to 24-hour format
      const hour24 =
        ampm === "PM" ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;

      const finalDate = selectedDate
        .hour(hour24)
        .minute(minute)
        .second(0)
        .millisecond(0);

      // Validate that the selected time is not in the past
      if (finalDate.isBefore(dayjs())) {
        toast.error("You cannot select a past time!");
        return;
      }

      returnDateTime(finalDate.toISOString());
      onClose();
      console.log("Selected DateTime:", finalDate.toISOString());
    }
  };

  const handleBack = () => {
    if (step === "time") {
      setStep("date");
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (previouslySelectedDate) {
      const extractedDate = dayjs(previouslySelectedDate);
      setSelectedDate(extractedDate);
      setHour(extractedDate.hour() % 12 || 12);
      setMinute(extractedDate.minute());
      setAmPm(extractedDate.hour() >= 12 ? "PM" : "AM");
    }
  }, [previouslySelectedDate]);

  return (
    <div className="date-time-picker">
      {step === "date" ? (
        <Calendar
          fullscreen={false}
          onSelect={onSelectDate}
          value={selectedDate || undefined}
          disabledDate={disabledDate}
          headerRender={({ value, onChange }) => {
            const year = value.year();
            const month = value.month();

            const yearOptions = Array.from({ length: 20 }, (_, i) => {
              const label = year - 10 + i;
              return { label, value: label };
            });

            const monthOptions = dayjs
              .localeData()
              .monthsShort()
              .map((label: any, index: any) => ({
                label,
                value: index,
              }));

            return (
              <div className="calendar-header">
                <Typography.Title level={5}>
                  {selectedDate ? selectedDate.format("DD MMM YYYY") : ""}
                </Typography.Title>
                <Flex gap={8}>
                  <Select
                    size="small"
                    popupMatchSelectWidth={false}
                    value={year}
                    options={yearOptions}
                    onChange={(newYear) => {
                      const now = value.clone().year(newYear);
                      onChange(now);
                    }}
                  />
                  <Select
                    size="small"
                    popupMatchSelectWidth={false}
                    value={month}
                    options={monthOptions}
                    onChange={(newMonth) => {
                      const now = value.clone().month(newMonth);
                      onChange(now);
                    }}
                  />
                </Flex>
              </div>
            );
          }}
        />
      ) : (
        <Flex vertical>
          <div className="time-select-header">Select Time</div>
          <Flex justify="space-between" gap={8}>
            <Select
              style={{ flex: 1 }}
              value={hour}
              onChange={setHour}
              options={getHourOptions()}
              placeholder="Hour"
            />
            <Select
              style={{ flex: 1 }}
              value={minute}
              onChange={setMinute}
              options={getMinuteOptions()}
              placeholder="Min"
            />
            <Select
              style={{ flex: 1 }}
              value={ampm}
              onChange={(v: "AM" | "PM") => setAmPm(v)}
              options={[
                { label: "AM", value: "AM" },
                { label: "PM", value: "PM" },
              ]}
            />
          </Flex>
        </Flex>
      )}

      <Flex justify="end" style={{ marginTop: 10 }}>
        <Button type="text" onClick={handleBack} style={{ color: "#722ed1" }}>
          {step === "time" ? "Back" : "Cancel"}
        </Button>
        <Button type="text" onClick={handleNext} style={{ color: "#722ed1" }}>
          {step === "date" ? "Next" : "Confirm"}
        </Button>
      </Flex>
    </div>
  );
};

export default DateTimePicker;
